import {
  type RemixiconComponentType,
  RiBox3Fill,
  RiCheckboxCircleFill,
  RiEditCircleFill,
  RiGridFill,
  RiInboxArchiveFill,
  RiRouteFill,
  RiRuler2Fill,
  RiScissorsLine,
  RiStackFill,
  RiTruckFill,
  RiTruckLine
} from '@remixicon/react'

import type { DetailStatus } from '../selectors/detail-status-selector/config/statuses'

import { cn } from '@/lib/utils'

interface DetailStatusBadgeProps extends React.ComponentProps<'div'> {
  status: DetailStatus
  className?: string
  done?: boolean
}

interface StatusConfig {
  icon: RemixiconComponentType
  triggerClassName: string
  itemClassName: string
  name: string
}

export const STATUS_CONFIG: Record<DetailStatus, StatusConfig> = {
  cut: {
    icon: RiScissorsLine,
    triggerClassName: 'bg-blue-100 [&_svg]:text-blue-200 text-black-200',
    itemClassName: '[&_svg]:text-blue-200',
    name: 'Cut'
  },
  cutting: {
    icon: RiScissorsLine,
    triggerClassName: 'bg-blue-100 [&_svg]:text-blue-200 text-black-200',
    itemClassName: '[&_svg]:text-blue-200',
    name: 'Cutting'
  },
  punching: {
    icon: RiEditCircleFill,
    triggerClassName: 'bg-red-100 [&_svg]:text-red-300 text-black-200 border-red-200',
    itemClassName: '[&_svg]:text-red-300',
    name: 'Punching'
  },
  punched: {
    icon: RiEditCircleFill,
    triggerClassName: 'bg-red-100 [&_svg]:text-red-300 text-black-200 border-red-200',
    itemClassName: '[&_svg]:text-red-300',
    name: 'Punched'
  },
  varionbend: {
    icon: RiRouteFill,
    triggerClassName: 'bg-cyan-100 [&_svg]:text-cyan-200 text-black-200 border-cyan-200',
    itemClassName: '[&_svg]:text-cyan-200',
    name: 'Variobend'
  },
  packaging: {
    icon: RiInboxArchiveFill,
    triggerClassName: 'bg-purple-100 [&_svg]:text-purple-200 text-black-200 border-purple-200',
    itemClassName: '[&_svg]:text-purple-200',
    name: 'Packaging'
  },
  ready_for_pack: {
    icon: RiInboxArchiveFill,
    triggerClassName: 'bg-purple-100 [&_svg]:text-purple-200 text-black-200 border-purple-200',
    itemClassName: '[&_svg]:text-purple-200',
    name: 'Ready for pack'
  },
  forming: {
    icon: RiRouteFill,
    triggerClassName: 'bg-yellow-100 [&_svg]:text-yellow-200 text-black-200 border-yellow-200',
    itemClassName: '[&_svg]:text-yellow-200',
    name: 'Forming'
  },
  bending: {
    icon: RiRouteFill,
    triggerClassName: 'bg-yellow-100 [&_svg]:text-yellow-200 text-black-200 border-yellow-200',
    itemClassName: '[&_svg]:text-yellow-200',
    name: 'Bending'
  },
  siding: {
    icon: RiStackFill,
    triggerClassName: 'bg-orange-100 [&_svg]:text-orange-300 text-black-200 border-orange-300',
    itemClassName: '[&_svg]:text-orange-300',
    name: 'Siding'
  },
  siding_done: {
    icon: RiCheckboxCircleFill,
    triggerClassName: 'bg-orange-100 [&_svg]:text-orange-300 text-black-200 border-orange-300',
    itemClassName: '[&_svg]:text-orange-300',
    name: 'Siding done'
  },
  trim: {
    icon: RiRuler2Fill,
    triggerClassName: 'bg-blue-100 [&_svg]:text-blue-300 text-black-200 border-blue-300',
    itemClassName: '[&_svg]:text-blue-300',
    name: 'Trim'
  },
  trim_done: {
    icon: RiCheckboxCircleFill,
    triggerClassName: 'bg-blue-100 [&_svg]:text-blue-300 text-black-200 border-blue-300',
    itemClassName: '[&_svg]:text-blue-300',
    name: 'Trim done'
  },
  soffit: {
    icon: RiGridFill,
    triggerClassName: 'bg-purple-100 [&_svg]:text-purple-200 text-black-200 border-purple-200',
    itemClassName: '[&_svg]:text-purple-200',
    name: 'Soffit'
  },
  soffit_done: {
    icon: RiCheckboxCircleFill,
    triggerClassName: 'bg-purple-100 [&_svg]:text-purple-200 text-black-200 border-purple-200',
    itemClassName: '[&_svg]:text-purple-200',
    name: 'Soffit done'
  },
  complete: {
    icon: RiCheckboxCircleFill,
    triggerClassName: 'bg-green-100 [&_svg]:text-green-200 text-black-200 border-green-200',
    itemClassName: '[&_svg]:text-green-200',
    name: 'Completed'
  },
  ready_to_ship: {
    icon: RiTruckLine,
    triggerClassName: 'bg-orange-100 [&_svg]:text-orange-200 text-black-200 border-orange-200',
    itemClassName: '[&_svg]:text-orange-200',
    name: 'Ready to Ship'
  },
  shipped: {
    icon: RiTruckFill,
    triggerClassName: 'bg-purple-200 [&_svg]:text-white-100 text-white-100 border-purple-200',
    itemClassName: '[&_svg]:text-white-100',
    name: 'Shipped'
  }
}
export const DetailStatusBadge = ({ status, className, ...props }: DetailStatusBadgeProps) => {
  const currentStatus = STATUS_CONFIG?.[status]

  return (
    <div
      className={cn(
        'flex h-6 flex-1 items-center justify-center gap-1 rounded-full',
        currentStatus ? currentStatus?.triggerClassName : 'bg-grey-100 [&_svg]:text-grey-400 text-black-200',
        className
      )}
      {...props}
    >
      {currentStatus ? <currentStatus.icon className='size-4' /> : <RiBox3Fill className='size-4' />}
      {currentStatus?.name ?? ''}
    </div>
  )
}
